import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  formatAlertDate,
  severityMeta,
  statusMeta,
  vendingTableBodyCellSx,
  vendingTableHeadCellSx,
} from './alertDisplayUtils';
import vendingApiClient from '../../Services/VendingApi';
import { COLORS } from '../../Styles/Colors';
import {
  VendingAlert,
  VendingAlertListResponse,
  VendingAlertSeverity,
} from '../../Types/vending';

type StatusFilter = 'open' | 'acknowledged' | 'resolved' | 'all';
type SeverityFilter = '' | VendingAlertSeverity;

interface MachineAlertsTabProps {
  machineId: string;
}

export const MachineAlertsTab = ({ machineId }: MachineAlertsTabProps) => {
  const [alerts, setAlerts] = useState<VendingAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('');
  const [searchInput, setSearchInput] = useState('');
  const [actingId, setActingId] = useState<number | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesAction, setNotesAction] = useState<'acknowledge' | 'resolve'>('acknowledge');
  const [notesAlert, setNotesAlert] = useState<VendingAlert | null>(null);
  const [notes, setNotes] = useState('');

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200', status: statusFilter });
      if (severityFilter) params.set('severity', severityFilter);
      const response = await vendingApiClient(`/machines/${machineId}/alerts?${params.toString()}`);
      const json: VendingAlertListResponse = await response.json();
      setAlerts(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [machineId, statusFilter, severityFilter]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const openNotesDialog = (alert: VendingAlert, action: 'acknowledge' | 'resolve') => {
    setNotesAlert(alert);
    setNotesAction(action);
    setNotes(alert.notes || '');
    setNotesOpen(true);
  };

  const submitNotesAction = async () => {
    if (!notesAlert) return;
    setActingId(notesAlert.id);
    setActionMessage(null);
    try {
      await vendingApiClient(`/alerts/${notesAlert.id}/${notesAction}`, {
        method: 'POST',
        body: JSON.stringify({ notes: notes.trim() || undefined }),
      });
      setNotesOpen(false);
      setNotesAlert(null);
      setNotes('');
      setActionMessage({
        text: notesAction === 'acknowledge' ? 'Alert acknowledged.' : 'Alert resolved.',
        severity: 'success',
      });
      await loadAlerts();
    } catch (err) {
      setActionMessage({
        text: err instanceof Error ? err.message : `Failed to ${notesAction} alert`,
        severity: 'error',
      });
    } finally {
      setActingId(null);
    }
  };

  const filteredAlerts = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return alerts;
    return alerts.filter((a) => {
      const hay = [a.title, a.description, a.alert_type_label].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [alerts, searchInput]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 2,
          flexShrink: 0,
        }}
      >
        <TextField
          size="small"
          label="Search"
          placeholder="Alert title or type…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 200, flex: { xs: '1 1 100%', md: '0 1 240px' } }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="acknowledged">Acknowledged</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Severity</InputLabel>
          <Select
            label="Severity"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="info">Info</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={loadAlerts} disabled={loading} sx={{ ml: 'auto' }}>
          Refresh
        </Button>
      </Box>

      {actionMessage && (
        <Alert severity={actionMessage.severity} sx={{ mb: 2, flexShrink: 0 }} onClose={() => setActionMessage(null)}>
          {actionMessage.text}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: COLORS.teal }} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadAlerts}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && filteredAlerts.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
            borderRadius: 2,
            border: `1px dashed ${COLORS.lightGray}`,
            backgroundColor: '#fafbfc',
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 1 }}>
            No alerts
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
            {statusFilter === 'open' && !searchInput.trim()
              ? 'This machine has no open alerts.'
              : 'No alerts match the current filters.'}
          </Typography>
        </Box>
      )}

      {!loading && !error && filteredAlerts.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${COLORS.lightGray}40`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="medium" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 110 }}>Severity</TableCell>
                  <TableCell sx={vendingTableHeadCellSx}>Alert</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 160 }}>Last detected</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 120 }}>Status</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 200, textAlign: 'right' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAlerts.map((alert) => {
                  const sev = severityMeta(alert.severity);
                  const st = statusMeta(alert.status);
                  const busy = actingId === alert.id;
                  return (
                    <TableRow key={alert.id} hover sx={{ '&:hover': { backgroundColor: '#fafbfc' } }}>
                      <TableCell sx={vendingTableBodyCellSx}>
                        <Chip
                          size="small"
                          label={sev.label}
                          sx={{ fontWeight: 700, bgcolor: sev.bg, color: sev.color }}
                        />
                      </TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>
                        <Typography variant="body2" fontWeight={600}>{alert.title}</Typography>
                        {alert.description && (
                          <Typography variant="caption" sx={{ color: COLORS.lightGray, display: 'block', mt: 0.25 }}>
                            {alert.description}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: COLORS.teal, display: 'block', mt: 0.25 }}>
                          {alert.alert_type_label}
                        </Typography>
                      </TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {formatAlertDate(alert.last_detected)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>
                        <Chip
                          size="small"
                          label={alert.status}
                          sx={{
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            bgcolor: st.bg,
                            color: st.color,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...vendingTableBodyCellSx, textAlign: 'right' }}>
                        {alert.status === 'open' && (
                          <Button
                            size="small"
                            disabled={busy}
                            onClick={() => openNotesDialog(alert, 'acknowledge')}
                            sx={{ mr: 1 }}
                          >
                            Acknowledge
                          </Button>
                        )}
                        {alert.status !== 'resolved' && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busy}
                            onClick={() => openNotesDialog(alert, 'resolve')}
                          >
                            Resolve
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {notesAction === 'acknowledge' ? 'Acknowledge alert' : 'Resolve alert'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: COLORS.lightGray }}>
            {notesAlert?.title}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitNotesAction} disabled={actingId != null}>
            {notesAction === 'acknowledge' ? 'Acknowledge' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MachineAlertsTab;
